import { defineStore } from "pinia";
import { computed, ComputedRef, ref } from "vue";
import { userAccountsService } from "@/services/user-accounts.service";

type Card = {
  name?: string;
  text: string;
  value: number;
};

export interface CardData {
  name: string;
  text1: string;
  text2: string;
  text3: string;
}

type StatCard = {
  card1: Card;
  card2: Card;
  card3: Card;
};

export interface StatsState {
  statCard: StatCard;
}

const defaultState = { name: "", text: "", value: 0 };

export const useAccountManagementStore = defineStore("stats", () => {
  // STATES
  const statCard = ref<StatsState["statCard"]>({
    card1: defaultState,
    card2: defaultState,
    card3: defaultState,
  });
  const memberStatus = ref<boolean>(false);

  // ACTIONS
  const resetCardData = (cardDetails: CardData) => {
    statCard.value.card1 = { text: cardDetails.text1, value: 0 };
    statCard.value.card2 = { text: cardDetails.text2, value: 0 };
    statCard.value.card3 = { text: cardDetails.text3, value: 0 };
  };

  const setJobCardData = (cardDetails: StatCard) => {
    statCard.value.card1 = cardDetails.card1;
    statCard.value.card2 = cardDetails.card2;
    statCard.value.card3 = cardDetails.card3;
  };

  const setCardData = async (cardDetails: CardData) => {
    await userAccountsService
      .fetchStatsData(cardDetails)
      .then((response: any) => {
        if (response) {
          statCard.value.card1 = {
            text: cardDetails.text1,
            value: response.data.allowance ?? 0,
          };
          statCard.value.card2 = {
            text: cardDetails.text2,
            value: response.data.active ?? 0,
          };
          statCard.value.card3 = {
            text: cardDetails.text3,
            value: response.data.provisioning ?? 0,
          };
        } else {
          resetCardData(cardDetails);
        }
      })
      .catch(() => {
        resetCardData(cardDetails);
      });
  };

  const setMemberStatus = (memberData: any) => {
    memberStatus.value =
      memberData.status == "ACTIVE" || memberData.status == "REJECTED";
  };

  //GETTERS
  const getCardData: ComputedRef<StatsState["statCard"]> = computed(
    () => statCard.value,
  );
  const getAddShowButton: ComputedRef<boolean> = computed(() => {
    const { card1, card2 } = getCardData.value;
    return card1.value != 0 || (card1.value > 0 && card2.value == card1.value);
  });

  const getMemberStatus: ComputedRef<boolean> = computed(
    () => memberStatus.value,
  );

  return {
    setCardData,
    setJobCardData,
    setMemberStatus,
    getCardData,
    getAddShowButton,
    getMemberStatus,
  };
});
